import React, { useState } from 'react';
import apiService from '../services/api';
import { SUBSCRIPTION_PLANS, PLAN_DETAILS } from '../config/stripe';

interface GymFormData {
  // Gym Information
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;

  // Owner Information
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPhone: string;

  // Business Information
  businessType: 'gym' | 'crossfit' | 'yoga' | 'martial_arts' | 'dance' | 'other';
  establishedDate: string;
  // Subscription Information
  subscriptionPriceId: string;
}

const CEOEnrollmentPage: React.FC = () => {
  const [formData, setFormData] = useState<GymFormData>({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    ownerFirstName: '',
    ownerLastName: '',
    ownerEmail: '',
    ownerPhone: '',
    businessType: 'gym',
    establishedDate: '',
    subscriptionPriceId: SUBSCRIPTION_PLANS.STANDARD // Default to standard plan
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [enrollmentResult, setEnrollmentResult] = useState<any>(null);

  const businessTypes = [
    { value: 'gym', label: 'Traditional Gym' },
    { value: 'crossfit', label: 'CrossFit Box' },
    { value: 'yoga', label: 'Yoga Studio' },
    { value: 'martial_arts', label: 'Martial Arts' },
    { value: 'dance', label: 'Dance Studio' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field: keyof GymFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Gym Information
    if (!formData.name.trim()) newErrors.name = 'Gym name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';

    // Owner Information
    if (!formData.ownerFirstName.trim()) newErrors.ownerFirstName = 'Owner first name is required';
    if (!formData.ownerLastName.trim()) newErrors.ownerLastName = 'Owner last name is required';
    if (!formData.ownerEmail.trim()) newErrors.ownerEmail = 'Owner email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.ownerEmail)) newErrors.ownerEmail = 'Please enter a valid email';
    if (!formData.ownerPhone.trim()) newErrors.ownerPhone = 'Owner phone is required';

    // Business Information
    if (!formData.businessType) newErrors.businessType = 'Business type is required';
    if (!formData.establishedDate) newErrors.establishedDate = 'Established date is required';

    // Subscription Information
    if (!formData.subscriptionPriceId) newErrors.subscriptionPriceId = 'Subscription plan is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});
    
    try {
      const result = await apiService.enrollGym(formData);
      setEnrollmentResult(result);
      setSuccess(true);
      
      // Reset form after successful submission
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        ownerFirstName: '',
        ownerLastName: '',
        ownerEmail: '',
        ownerPhone: '',
        businessType: 'gym',
        establishedDate: '',
        subscriptionPriceId: SUBSCRIPTION_PLANS.STANDARD
      });
    } catch (error: any) {
      console.error('Error enrolling gym:', error);
      
      // Handle validation errors
      if (error.response?.data?.details) {
        const fieldErrors: Record<string, string> = {};
        error.response.data.details.forEach((err: any) => {
          fieldErrors[err.field] = err.message;
        });
        setErrors(fieldErrors);
      } else {
        setErrors({ submit: error.response?.data?.error || 'Failed to enroll gym. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success screen
  if (success && enrollmentResult) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Gym Enrolled Successfully!</h1>
          <p className="mt-2 text-gray-300">
            The gym has been added to your network and the owner has been notified.
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">Enrollment Complete</h2>
            
            <div className="bg-white/5 rounded-lg p-6 mb-6 text-left">
              <h3 className="text-lg font-semibold text-white mb-4">Gym Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Gym Name:</span>
            <span className="text-white ml-2">{enrollmentResult.gym.name}</span>
          </div>
          <div>
            <span className="text-gray-400">Contact Email:</span>
            <span className="text-white ml-2">{enrollmentResult.gym.email}</span>
          </div>
          <div>
            <span className="text-gray-400">Owner Login:</span>
            <span className="text-white ml-2">{enrollmentResult.owner.email}</span>
          </div>
          <div>
            <span className="text-gray-400">Temporary Password:</span>
            <span className="text-yellow-400 ml-2 font-mono">{enrollmentResult.owner.tempPassword}</span>
          </div>
        </div>
            </div>

            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-yellow-200 text-sm">
                <strong>Important:</strong> The gym owner should change their password on first login. 
                Share the temporary password securely with them.
              </p>
            </div>

            <button
              onClick={() => {
                setSuccess(false);
                setEnrollmentResult(null);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Enroll Another Gym
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Enroll New Gym</h1>
        <p className="mt-2 text-gray-300">
          Add a new gym to your network. The gym owner will receive admin access to manage their gym.
        </p>
      </div>

      {/* Error Display */}
      {errors.submit && (
        <div className="mb-6 bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-200">{errors.submit}</p>
        </div>
      )}

      {/* Form */}
      <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Gym Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-white mb-4">Gym Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Gym Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                  errors.name ? 'border-red-500' : 'border-white/20'
                }`}
                placeholder="Enter gym name"
              />
              {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Address *
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                  errors.address ? 'border-red-500' : 'border-white/20'
                }`}
                placeholder="Street address"
              />
              {errors.address && <p className="mt-1 text-sm text-red-400">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                    errors.city ? 'border-red-500' : 'border-white/20'
                  }`}
                  placeholder="City"
                />
                {errors.city && <p className="mt-1 text-sm text-red-400">{errors.city}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                    errors.state ? 'border-red-500' : 'border-white/20'
                  }`}
                  placeholder="State"
                />
                {errors.state && <p className="mt-1 text-sm text-red-400">{errors.state}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                    errors.zipCode ? 'border-red-500' : 'border-white/20'
                  }`}
                  placeholder="ZIP Code"
                />
                {errors.zipCode && <p className="mt-1 text-sm text-red-400">{errors.zipCode}</p>}
              </div>
            </div>

          </div>

          {/* Owner Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-white mb-4">Owner Information</h3>
            <p className="text-sm text-gray-400 mb-4">
              The gym owner will receive admin access to manage their gym through the admin portal.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.ownerFirstName}
                  onChange={(e) => handleInputChange('ownerFirstName', e.target.value)}
                  className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                    errors.ownerFirstName ? 'border-red-500' : 'border-white/20'
                  }`}
                  placeholder="First name"
                />
                {errors.ownerFirstName && <p className="mt-1 text-sm text-red-400">{errors.ownerFirstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.ownerLastName}
                  onChange={(e) => handleInputChange('ownerLastName', e.target.value)}
                  className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                    errors.ownerLastName ? 'border-red-500' : 'border-white/20'
                  }`}
                  placeholder="Last name"
                />
                {errors.ownerLastName && <p className="mt-1 text-sm text-red-400">{errors.ownerLastName}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={formData.ownerEmail}
                onChange={(e) => handleInputChange('ownerEmail', e.target.value)}
                className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                  errors.ownerEmail ? 'border-red-500' : 'border-white/20'
                }`}
                placeholder="owner@example.com"
              />
              {errors.ownerEmail && <p className="mt-1 text-sm text-red-400">{errors.ownerEmail}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Phone *
              </label>
              <input
                type="tel"
                value={formData.ownerPhone}
                onChange={(e) => handleInputChange('ownerPhone', e.target.value)}
                className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400 ${
                  errors.ownerPhone ? 'border-red-500' : 'border-white/20'
                }`}
                placeholder="Phone number"
              />
              {errors.ownerPhone && <p className="mt-1 text-sm text-red-400">{errors.ownerPhone}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Business Type *
                </label>
                <select
                  value={formData.businessType}
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white ${
                    errors.businessType ? 'border-red-500' : 'border-white/20'
                  }`}
                >
                  {businessTypes.map((type) => (
                    <option key={type.value} value={type.value} className="bg-gray-800 text-white">
                      {type.label}
                    </option>
                  ))}
                </select>
                {errors.businessType && <p className="mt-1 text-sm text-red-400">{errors.businessType}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Established Date *
                </label>
                <input
                  type="date"
                  value={formData.establishedDate}
                  onChange={(e) => handleInputChange('establishedDate', e.target.value)}
                  className={`w-full px-3 py-2 bg-white/10 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white ${
                    errors.establishedDate ? 'border-red-500' : 'border-white/20'
                  }`}
                />
                {errors.establishedDate && <p className="mt-1 text-sm text-red-400">{errors.establishedDate}</p>}
              </div>
            </div>

            {/* Subscription Plan Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Subscription Plan *
              </label>
              <div className="space-y-2">
                {Object.entries(PLAN_DETAILS).map(([priceId, plan]) => (
                  <label
                    key={priceId}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                      formData.subscriptionPriceId === priceId
                        ? 'bg-blue-500/20 border-blue-400'
                        : 'bg-white/5 border-white/20 hover:bg-white/10'
                    }`}
                  >
                    <input
                      type="radio"
                      name="subscriptionPriceId"
                      value={priceId}
                      checked={formData.subscriptionPriceId === priceId}
                      onChange={(e) => handleInputChange('subscriptionPriceId', e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="text-white font-medium">{plan.name}</div>
                      <div className="text-sm text-gray-300">
                        ${plan.price}/{plan.interval} - {plan.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.subscriptionPriceId && <p className="mt-1 text-sm text-red-400">{errors.subscriptionPriceId}</p>}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end mt-8 pt-6 border-t border-white/20">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-8 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:from-green-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isSubmitting ? 'Enrolling Gym...' : 'Enroll Gym'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CEOEnrollmentPage;